import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  collection,
  getDocs,
  limit,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { auth, db } from "../../firebaseConfig";

export default function CrearCuentaWeb() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");

  const [step, setStep] = useState("check"); // check | create
  const [checkedUid, setCheckedUid] = useState(null);

  const [info, setInfo] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const canCheck = useMemo(() => {
    return email.trim().length > 3 && !loading;
  }, [email, loading]);

  const canCreate = useMemo(() => {
    if (loading) return false;
    if (email.trim().length <= 3) return false;
    if (pass.length < 6) return false;
    if (pass !== pass2) return false;
    if (!checkedUid) return false;
    return true;
  }, [email, pass, pass2, loading, checkedUid]);

  async function checkEmailExistsInUsuarios(e) {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);

    try {
      const correo = email.trim().toLowerCase();

      // ✅ Busca en Firestore por campo "correo"
      const q = query(
        collection(db, "usuarios"),
        where("correo", "==", correo),
        limit(1)
      );

      const snap = await getDocs(q);

      if (snap.empty) {
        setCheckedUid(null);
        setStep("check");
        setInfo(
          "Ese correo no está autorizado todavía. Primero inicia sesión en la app y solicita ser vendedor."
        );
        return;
      }

      const userDoc = snap.docs[0];
      setCheckedUid(userDoc.id);

      // ✅ Aquí decides la regla real:
      const data = userDoc.data() || {};

      // Ejemplo: admin === true (como tu login admin)
      const autorizado = data?.solicitudVentaEstado === "aprobado";
      if (!autorizado) {
        setInfo(
          "Tu cuenta existe, pero aún no está habilitada para el panel web. Entra a la app y solicita ser vendedor."
        );
        setStep("check");
        setCheckedUid(null);
        return;
      }

      setStep("create");
      setInfo("Correo validado. Ahora crea tu contraseña para acceder al panel web.");
    } catch (err) {
      console.error(err);
      setError("No se pudo verificar el correo. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  }

  async function createWebPassword(e) {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);

    try {
      const correo = email.trim().toLowerCase();

      // ✅ Creamos cuenta Email/Pass (si no existe en Auth)
      const cred = await createUserWithEmailAndPassword(auth, correo, pass);
      const uid = cred.user?.uid;

      if (!uid) {
        setError("No se pudo crear la cuenta.");
        await signOut(auth);
        return;
      }

      // ✅ Seguridad: valida que el uid recién creado sea admin (según tu regla)
      // OJO: aquí normalmente no existirá doc usuarios/{uid} todavía,
      // porque tu doc estaba en otro uid (del login social).
      // Por eso hacemos 2 protecciones:
      // 1) verificamos que el correo estaba autorizado (ya lo hicimos)
      // 2) opcional: puedes crear un doc "usuariosWeb/{uid}" o escribir una marca
      // Para no complicarte hoy, hacemos login directo y te mandamos a /admin
      // y mantienes la validación en App.js (admin check).
      // Si App.js te saca, entonces no pasó la validación.

      navigate("/admin", { replace: true });
    } catch (err) {
      console.error(err);

      const code = err?.code || "";

      if (code === "auth/email-already-in-use") {
        setError(
          "Ese correo ya tiene una cuenta en MyShopCity (Google/Apple). Para poner contraseña: inicia sesión con Google/Apple en la web y luego crea contraseña (link)."
        );
        return;
      }

      if (code === "auth/weak-password") {
        setError("La contraseña es muy débil. Usa al menos 6 caracteres.");
        return;
      }

      setError("No se pudo crear la cuenta. Revisa los datos e intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.bgGlowA} aria-hidden="true" />
      <div style={styles.bgGlowB} aria-hidden="true" />

      <div style={styles.wrap}>
        {/* Brand */}
        <div style={styles.brand}>
          <div style={styles.logoWrap}>
            <img
              src="/logo.png"
              alt="MyShopCity"
              style={styles.logoImg}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                const fallback = e.currentTarget.nextSibling;
                if (fallback) fallback.style.display = "flex";
              }}
            />
            <div style={{ ...styles.logoFallback, display: "none" }}>MSC</div>
          </div>

          <div style={styles.brandText}>
            <div style={styles.brandName}>MyShopCity</div>
            <div style={styles.brandTag}>Crear acceso web</div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h1 style={styles.h1}>
              {step === "check" ? "Verificar correo" : "Crear contraseña"}
            </h1>
            <p style={styles.sub}>
              {step === "check"
                ? "Escribe tu correo. Si tu cuenta ya está autorizada, podrás crear una contraseña para entrar al panel."
                : "Listo. Ahora crea tu contraseña para usar Email/Contraseña en la web."}
            </p>
          </div>

          {/* Step 1 */}
          {step === "check" && (
            <form onSubmit={checkEmailExistsInUsuarios} style={styles.form}>
              <label style={styles.label}>
                Correo
                <input
                  style={styles.input}
                  type="email"
                  placeholder="tu@correo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </label>

              {info && (
                <div style={styles.infoBox}>
                  <div style={styles.infoTitle}>Información</div>
                  <div style={styles.infoText}>{info}</div>
                </div>
              )}

              {error && (
                <div style={styles.errorBox}>
                  <div style={styles.errorTitle}>No se pudo continuar</div>
                  <div style={styles.errorText}>{error}</div>
                </div>
              )}

              <button type="submit" disabled={!canCheck} style={styles.primaryBtn}>
                {loading ? (
                  <span style={styles.btnRow}>
                    <span style={styles.spinner} aria-hidden="true" />
                    Verificando…
                  </span>
                ) : (
                  "Verificar"
                )}
              </button>

              <div style={styles.dividerRow}>
                <div style={styles.divider} />
                <span style={styles.dividerText}>o</span>
                <div style={styles.divider} />
              </div>

              <button
                type="button"
                style={styles.secondaryBtn}
                onClick={() => navigate("/login")}
                disabled={loading}
              >
                Ya tengo contraseña → Iniciar sesión
              </button>

              <button
                type="button"
                style={styles.secondaryBtn}
                onClick={() => navigate("/", { replace: true })}
                disabled={loading}
              >
                Volver al inicio
              </button>

              <div style={styles.smallNote}>
                Si tu correo no aparece, entra a la app y solicita ser vendedor.
              </div>
            </form>
          )}

          {/* Step 2 */}
          {step === "create" && (
            <form onSubmit={createWebPassword} style={styles.form}>
              <div style={styles.pill}>
                Correo validado: <b style={{ marginLeft: 6 }}>{email.trim()}</b>
              </div>

              <label style={styles.label}>
                Contraseña
                <input
                  style={styles.input}
                  type="password"
                  placeholder="Mínimo 6 caracteres"
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </label>

              <label style={styles.label}>
                Confirmar contraseña
                <input
                  style={styles.input}
                  type="password"
                  placeholder="Repite tu contraseña"
                  value={pass2}
                  onChange={(e) => setPass2(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </label>

              {pass && pass2 && pass !== pass2 ? (
                <div style={styles.warnBox}>Las contraseñas no coinciden.</div>
              ) : null}

              {error && (
                <div style={styles.errorBox}>
                  <div style={styles.errorTitle}>No se pudo crear</div>
                  <div style={styles.errorText}>{error}</div>
                </div>
              )}

              <button type="submit" disabled={!canCreate} style={styles.primaryBtn}>
                {loading ? (
                  <span style={styles.btnRow}>
                    <span style={styles.spinner} aria-hidden="true" />
                    Creando…
                  </span>
                ) : (
                  "Crear acceso web"
                )}
              </button>

              <button
                type="button"
                style={styles.secondaryBtn}
                onClick={() => {
                  setStep("check");
                  setCheckedUid(null);
                  setPass("");
                  setPass2("");
                  setInfo("");
                  setError("");
                }}
                disabled={loading}
              >
                ← Cambiar correo
              </button>

              <div style={styles.smallNote}>
                Si tu correo ya está en Google/Apple, usa “Iniciar sesión” y luego te hago la pantalla para “Crear contraseña (link)”.
              </div>
            </form>
          )}
        </div>

        <div style={styles.footer}>
          © {new Date().getFullYear()} MyShopCity • Pagos seguros • Envíos incluidos
        </div>
      </div>
    </div>
  );
}

/* ---------------- Styles (mismo estilo del Login) ---------------- */
const styles = {
  page: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    padding: "28px 16px",
    background: "linear-gradient(180deg, #f7f7fb 0%, #ffffff 55%, #f7f7fb 100%)",
    position: "relative",
    overflow: "hidden",
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial',
    color: "#101114",
  },
  bgGlowA: {
    position: "absolute",
    top: -140,
    left: "50%",
    width: 720,
    height: 720,
    transform: "translateX(-55%)",
    background:
      "radial-gradient(circle, rgba(90,120,255,0.20) 0%, rgba(90,120,255,0.06) 35%, rgba(255,255,255,0) 70%)",
    pointerEvents: "none",
  },
  bgGlowB: {
    position: "absolute",
    top: -110,
    left: "50%",
    width: 560,
    height: 560,
    transform: "translateX(-10%)",
    background:
      "radial-gradient(circle, rgba(0,200,255,0.16) 0%, rgba(0,200,255,0.05) 35%, rgba(255,255,255,0) 70%)",
    pointerEvents: "none",
  },

  wrap: { width: "100%", maxWidth: 460, position: "relative", zIndex: 1 },

  brand: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    justifyContent: "center",
    marginBottom: 14,
  },
  logoWrap: {
    width: 86,
    height: 86,
    borderRadius: 22,
    border: "1px solid rgba(0,0,0,0.10)",
    background: "rgba(255,255,255,0.95)",
    boxShadow: "0 18px 55px rgba(0,0,0,0.08)",
    overflow: "hidden",
    display: "grid",
    placeItems: "center",
  },
  logoImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  logoFallback: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 950,
    letterSpacing: 1,
    color: "rgba(16,17,20,0.82)",
    fontSize: 22,
  },
  brandText: { textAlign: "left" },
  brandName: { fontWeight: 950, fontSize: 18, letterSpacing: -0.2, lineHeight: 1.1 },
  brandTag: { fontSize: 12, opacity: 0.7, marginTop: 4 },

  card: {
    borderRadius: 18,
    border: "1px solid rgba(0,0,0,0.10)",
    background: "rgba(255,255,255,0.90)",
    boxShadow: "0 18px 65px rgba(0,0,0,0.10)",
    padding: 16,
  },
  cardHeader: { padding: "6px 4px 10px" },
  h1: { margin: 0, fontSize: 22, fontWeight: 950, letterSpacing: -0.3 },
  sub: { margin: "6px 0 0", fontSize: 13, opacity: 0.75, lineHeight: 1.5 },

  form: { display: "grid", gap: 12, marginTop: 8 },

  label: { fontSize: 12, opacity: 0.8, fontWeight: 800, display: "grid", gap: 8 },
  input: {
    padding: "12px 12px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "rgba(255,255,255,0.95)",
    outline: "none",
    fontSize: 14,
  },

  pill: {
    borderRadius: 999,
    padding: "10px 12px",
    border: "1px solid rgba(0,0,0,0.10)",
    background: "rgba(0,0,0,0.03)",
    fontSize: 13,
    fontWeight: 800,
    color: "rgba(16,17,20,0.82)",
  },

  infoBox: {
    borderRadius: 14,
    border: "1px solid rgba(70,100,245,0.22)",
    background: "rgba(70,100,245,0.08)",
    padding: 12,
  },
  infoTitle: { fontWeight: 950, fontSize: 13, marginBottom: 4 },
  infoText: { fontSize: 13, opacity: 0.85, lineHeight: 1.45 },

  warnBox: {
    borderRadius: 14,
    border: "1px solid rgba(245,158,11,0.28)",
    background: "rgba(245,158,11,0.12)",
    padding: 12,
    fontSize: 13,
    fontWeight: 850,
  },

  errorBox: {
    borderRadius: 14,
    border: "1px solid rgba(255,60,60,0.22)",
    background: "rgba(255,60,60,0.08)",
    padding: 12,
  },
  errorTitle: { fontWeight: 950, fontSize: 13, marginBottom: 4 },
  errorText: { fontSize: 13, opacity: 0.85, lineHeight: 1.45 },

  primaryBtn: {
    padding: "12px 14px",
    borderRadius: 14,
    border: "1px solid rgba(90,120,255,0.25)",
    background:
      "linear-gradient(180deg, rgba(90,120,255,0.92), rgba(70,100,245,0.92))",
    color: "white",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 18px 55px rgba(90,120,255,0.22)",
  },

  secondaryBtn: {
    padding: "12px 14px",
    borderRadius: 14,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "rgba(255,255,255,0.95)",
    color: "rgba(16,17,20,0.86)",
    fontWeight: 950,
    cursor: "pointer",
  },

  dividerRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    gap: 10,
    marginTop: 2,
  },
  divider: { height: 1, background: "rgba(0,0,0,0.10)" },
  dividerText: { fontSize: 12, opacity: 0.55, fontWeight: 900 },

  btnRow: { display: "inline-flex", gap: 10, alignItems: "center", justifyContent: "center" },
  spinner: {
    width: 16,
    height: 16,
    borderRadius: 999,
    border: "3px solid rgba(255,255,255,0.35)",
    borderTopColor: "rgba(255,255,255,0.95)",
    animation: "spin 0.9s linear infinite",
  },

  smallNote: { fontSize: 12, opacity: 0.6, textAlign: "center", marginTop: 2 },

  footer: { marginTop: 14, textAlign: "center", fontSize: 12, opacity: 0.65 },
};

// ✅ Animación del spinner
const styleTag = document.createElement("style");
styleTag.innerHTML = `@keyframes spin{to{transform:rotate(360deg)}}`;
document.head.appendChild(styleTag);